import React, { useState } from "react";
import { useHealthReports } from "../hooks/useHealthReports";
import { PencilLine } from "lucide-react";
import EditHealthReportDialog from "./edit-health-report-dialog";
import { HealthReport } from "src/redux/types/_customer.type";

const ViewHealthReport = ({ memberId }: { memberId: number }) => {
  const { data: reports = [], isPending } = useHealthReports(memberId!);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<HealthReport | null>(
    null,
  );

  const openEditDialog = (report: HealthReport | null) => {
    setSelectedReport(report);
    setIsOpen(true);
  };

  const dialogClose = () => {
    setIsOpen(false);
    setSelectedReport(null);
  };

  const minReports = 4;
  const displayReports = [...reports];
  while (displayReports.length < minReports) {
    displayReports.push(null);
  }

  if (isPending) {
    return (
      <div className="w-full py-12 flex justify-center items-center">
        <p className="p-4 text-center flex gap-3 items-center">
          <span>Loading health report </span>
          <span className="loading loading-dots loading-md"></span>
        </p>
      </div>
    );
  }

  const healthParams = [
    {
      label: "BLOOD PRESSURE (mmHg) Systolic",
      field: "blood_pressure_systolic",
    },
    {
      label: "BLOOD PRESSURE (mmHg) Diastolic",
      field: "blood_pressure_diastolic",
    },
    { label: "PULSE RATE (BPM)", field: "pulse_rate" },
    { label: "BLOOD SUGAR LEVEL (mg/dL) Random", field: "blood_sugar_random" },
    { label: "BODY WEIGHT (Kg)", field: "body_weight" },
    { label: "BMI", field: "bmi" },
    { label: "BODY FAT (%)", field: "body_fat" },
    { label: "MUSCLE RATE (%)", field: "muscle_rate" },
    { label: "BODY WATER (%)", field: "body_water" },
    { label: "BONE MASS (Kg)", field: "bone_mass" },
    { label: "BMR (Kcal)", field: "bmr" },
    { label: "METABOLIC AGE (Years)", field: "metabolic_age" },
    { label: "VISCERAL FAT (%)", field: "visceral_fat" },
    { label: "SUBCUTANEOUS FAT (%)", field: "subcutaneous_fat" },
    { label: "PROTEIN MASS (Kg)", field: "protein_mass" },
    { label: "MUSCLE MASS (Kg)", field: "muscle_mass" },
    { label: "WEIGHT WITHOUT FAT (Kg)", field: "weight_witout_fat" },
    { label: "OBESITY LEVEL", field: "obesity_level" },
    { label: "SKELETAL MUSCLE MASS (Kg)", field: "skeletal_muscle_mass" },
    { label: "IDEAL BODY WEIGHT (Kg)", field: "ideal_body_weight" },
    { label: "WATER WEIGHT (Kg)", field: "water_weight" },
    { label: "OBESITY GRADE", field: "obesity_grade" },
    { label: "FAT MASS (Kg)", field: "fat_mass" },
    { label: "BODY SCORE", field: "body_score" },
  ];

  const baseWidth = 100; // Width for the parameter column
  const reportColumnWidth = 350; // Width for each report column
  const minTableWidth = Math.max(
    baseWidth + displayReports.length * reportColumnWidth,
    1800,
  );

  return (
    <main className="flex flex-col px-2 pt-8 border rounded-lg">
      <h2 className="text-center mb-3 text-lg">Health Parameters</h2>
      {!memberId ? (
        <div className="">
          <p className="p-4 text-center">
            Select a Member to View Health Report
          </p>
        </div>
      ) : (
        <div>
          {/* Health Report Table */}
          <div className="overflow-x-auto w-full pr-3 mb-6">
            <table
              className={`min-w-[${minTableWidth}px] table-auto border-collapse border border-gray-400 text-xs`}
              style={{ minWidth: `${minTableWidth}px` }}
            >
              <tbody>
                {/* Header Row */}
                <tr className="bg-gray-100 sticky top-0 z-30">
                  <th className="border border-gray-300 px-2 py-1 text-center sticky left-0 bg-gray-100 z-10 w-[300px]">
                    Health Parameters
                  </th>
                  {displayReports.map((report, index) => {
                    const displayDate = report?.created_at
                      ? new Date(report.created_at).toLocaleDateString()
                      : "";
                    return (
                      <th
                        key={report?.id ?? `empty-${index}`}
                        className="border border-gray-300 px-2 py-1 text-center w-[300px]"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span>
                            Month {index + 1}
                            {displayDate && `, Date: ${displayDate}`}
                          </span>
                          <button
                            className="rounded-full p-1 border hover:bg-gray-200 shadow-sm transition-all"
                            onClick={() => openEditDialog(report)}
                            title="Edit Report"
                          >
                            <PencilLine className="size-3" />
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>

                {/* Health Params Rows */}
                {healthParams.map(({ label, field }, i) => (
                  <tr key={i}>
                    <td className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10">
                      {label}
                    </td>
                    {displayReports.map((report, index) => {
                      const displayValue = report
                        ? (report[field] ?? "--")
                        : "--";
                      return (
                        <td
                          key={`m${index}-${field}`}
                          className="border border-gray-300 px-2 py-1"
                          colSpan={1}
                        >
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* Other Parameters Section - Month Wise */}
                <tr>
                  <td className="border border-gray-300 px-2 py-1 text-center font-semibold sticky left-0 bg-white z-10">
                    Other Parameters
                  </td>
                  {displayReports.map((report, index) => (
                    <td
                      key={`other-params-${index}`}
                      className="border border-gray-300 p-0"
                      colSpan={1}
                    >
                      <div className="w-full">
                        <table className="w-full border-collapse">
                          <tbody>
                            <tr>
                              <td className="border border-gray-300 px-2 py-1 font-semibold w-[100px]">
                                Abnormal Blood Reports
                              </td>
                              <td className="border border-gray-300 px-2 py-1">
                                {report?.medical_history || "--"}
                              </td>
                            </tr>
                            <tr>
                              <td className="border border-gray-300 px-2 py-1 font-semibold">
                                Medication
                              </td>
                              <td className="border border-gray-300 px-2 py-1">
                                {report?.medication || "--"}
                              </td>
                            </tr>
                            <tr>
                              <td className="border border-gray-300 px-2 py-1 font-semibold">
                                Activity Level
                              </td>
                              <td className="border border-gray-300 px-2 py-1">
                                {report?.activity_level || "--"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedReport && (
        <EditHealthReportDialog
          report={selectedReport}
          memberId={memberId}
          open={isOpen}
          close={dialogClose}
        />
      )}
    </main>
  );
};

export default ViewHealthReport;
